/**
 * How long the page keeps drawing a character that left one stream before the same character turns up in another
 * (ADR-147 decision 15, PROTOCOL.md 5.2): a `crossed` record, an edge-view entity that is no longer in its view, and every
 * figure of the map left when the page's own character crosses.
 */
export const CROSSED_HOLD_MS = 300;

/**
 * What a collection is told when the figure held for an entity first drawn moves and the collection holds a single
 * record of it: wait for the next, the hold drawing the figure meanwhile.
 */
export const CLAIM_LATER = Symbol("claim later");

/**
 * A held figure taken back by the stream it turned up in: the figure and where its last motion has carried it, in
 * sixteenths of the page's frame.
 */
export interface Claimed<T> {
  figure: T;
  x: number;
  y: number;
}

/**
 * Poses a held figure for the frame: where its last motion carries it, in sixteenths of the page's frame, and whether it
 * is moving.
 */
export type HeldPose<T> = (figure: T, x: number, y: number, moving: boolean) => void;

/**
 * The key a player is matched by across two maps' streams, where its handle differs (ADR-147 decision 15: "the same
 * character, the spawn's name and family"): both parts, kept apart, or null for an entity with no name.
 *
 * @param first The first name.
 * @param family The family name.
 * @returns The key, or null when both parts are empty.
 */
export function nameKey(first: string, family: string): string | null {
  return first.length === 0 && family.length === 0 ? null : `${first.length}:${first}${family}`;
}

/**
 * The figures held through a crossing (ADR-147 decision 15): each is drawn on its last motion until a stream takes it
 * back by its key (`entityKey`, for the same map's stream or edge view) or its name (a player that crossed to the other
 * map, where its handle differs), or until `CROSSED_HOLD_MS` has passed, when it is let go. The slots are made once, so
 * holding, stepping and claiming allocate nothing.
 */
export class FigureHold<T> {
  private readonly figures: (T | null)[];
  private readonly xs: Float64Array;
  private readonly ys: Float64Array;
  private readonly vxs: Float64Array;
  private readonly vys: Float64Array;
  private readonly since: Float64Array;
  private readonly keys: Float64Array;
  private readonly names: (string | null)[];
  private held = 0;
  private readonly claimed: Claimed<T | null> = { figure: null, x: 0, y: 0 };

  /**
   * @param capacity How many figures it holds at once; a full hold lets its oldest go to take another.
   * @param release Lets a figure go: it was not taken back in time, or the hold was cleared.
   */
  public constructor(
    capacity: number,
    private readonly release: (figure: T) => void,
  ) {
    this.figures = new Array<T | null>(capacity).fill(null);
    this.names = new Array<string | null>(capacity).fill(null);
    this.xs = new Float64Array(capacity);
    this.ys = new Float64Array(capacity);
    this.vxs = new Float64Array(capacity);
    this.vys = new Float64Array(capacity);
    this.since = new Float64Array(capacity);
    this.keys = new Float64Array(capacity);
  }

  /**
   * How many figures are held.
   *
   * @returns The count.
   */
  public get size(): number {
    return this.held;
  }

  /**
   * Holds a figure that left its stream.
   *
   * @param figure The figure.
   * @param x Where it was drawn, across, in sixteenths of the page's frame.
   * @param y The same down.
   * @param vx Its last motion across, in sixteenths a millisecond.
   * @param vy The same down.
   * @param now The time, in milliseconds.
   * @param key Its `entityKey` in the stream it left.
   * @param name Its `nameKey`, or null for an entity that never crosses.
   */
  public offer(figure: T, x: number, y: number, vx: number, vy: number, now: number, key: number, name: string | null): void {
    let slot = this.figures.indexOf(null);
    if (slot < 0) {
      slot = this.oldest();
      this.release(this.figures[slot] as T);
      this.held -= 1;
    }
    this.figures[slot] = figure;
    this.xs[slot] = x;
    this.ys[slot] = y;
    this.vxs[slot] = vx;
    this.vys[slot] = vy;
    this.since[slot] = now;
    this.keys[slot] = key;
    this.names[slot] = name;
    this.held += 1;
  }

  /**
   * Takes back a held figure that has turned up in a stream: one held under the key first, else one held under the name.
   * The result is reused by the next claim.
   *
   * @param key The `entityKey` it turned up under.
   * @param name Its `nameKey`, or null.
   * @param now The time, in milliseconds.
   * @returns The figure and where its motion has carried it, or null when none is held under either or the hold ran out.
   */
  public claim(key: number, name: string | null, now: number): Readonly<Claimed<T>> | null {
    const slot = this.find(key, name, now);
    if (slot < 0) {
      return null;
    }
    const age = now - (this.since[slot] as number);
    const claimed = this.claimed;
    claimed.figure = this.figures[slot] as T;
    claimed.x = (this.xs[slot] as number) + (this.vxs[slot] as number) * age;
    claimed.y = (this.ys[slot] as number) + (this.vys[slot] as number) * age;
    this.empty(slot);
    return claimed as Claimed<T>;
  }

  /**
   * Whether a figure held under the key, else the name, is moving, without taking it: a collection waits for a second
   * record before it takes a moving one, so its sample lies on the path the hold carried it along.
   *
   * @param key The `entityKey` it turned up under.
   * @param name Its `nameKey`, or null.
   * @param now The time, in milliseconds.
   * @returns True when it moves, false when it stands, null when none is held or the hold ran out.
   */
  public moving(key: number, name: string | null, now: number): boolean | null {
    const slot = this.find(key, name, now);
    return slot < 0 ? null : (this.vxs[slot] as number) !== 0 || (this.vys[slot] as number) !== 0;
  }

  /**
   * The slot of a figure held under the key, else the newest held under the name, whose hold has not run out.
   *
   * @param key The key.
   * @param name The name key, or null.
   * @param now The time, in milliseconds.
   * @returns The slot, or -1.
   */
  private find(key: number, name: string | null, now: number): number {
    let slot = -1;
    for (let index = 0; index < this.figures.length; index += 1) {
      if (this.figures[index] !== null && this.keys[index] === key) {
        slot = index;
        break;
      }
    }
    if (slot < 0 && name !== null) {
      for (let index = 0; index < this.figures.length; index += 1) {
        if (this.figures[index] !== null && this.names[index] === name && (slot < 0 || (this.since[index] as number) > (this.since[slot] as number))) {
          slot = index;
        }
      }
    }
    return slot >= 0 && now - (this.since[slot] as number) <= CROSSED_HOLD_MS ? slot : -1;
  }

  /**
   * Poses every held figure on its last motion, and lets go of each held longer than `CROSSED_HOLD_MS`.
   *
   * @param now The time, in milliseconds.
   * @param pose Poses one figure.
   */
  public step(now: number, pose: HeldPose<T>): void {
    for (let slot = 0; slot < this.figures.length; slot += 1) {
      const figure = this.figures[slot];
      if (figure === null || figure === undefined) {
        continue;
      }
      const age = now - (this.since[slot] as number);
      if (age > CROSSED_HOLD_MS) {
        this.empty(slot);
        this.release(figure);
        continue;
      }
      const vx = this.vxs[slot] as number;
      const vy = this.vys[slot] as number;
      pose(figure, (this.xs[slot] as number) + vx * age, (this.ys[slot] as number) + vy * age, vx !== 0 || vy !== 0);
    }
  }

  /**
   * Moves every held figure by the change of the render origin (ADR-147 decision 6).
   *
   * @param dx Across, in sixteenths.
   * @param dy Down, in sixteenths.
   */
  public shift(dx: number, dy: number): void {
    for (let slot = 0; slot < this.figures.length; slot += 1) {
      this.xs[slot] = (this.xs[slot] as number) + dx;
      this.ys[slot] = (this.ys[slot] as number) + dy;
    }
  }

  /**
   * Lets every held figure go.
   */
  public clear(): void {
    for (let slot = 0; slot < this.figures.length; slot += 1) {
      const figure = this.figures[slot];
      if (figure !== null && figure !== undefined) {
        this.empty(slot);
        this.release(figure);
      }
    }
  }

  /**
   * Empties a slot.
   *
   * @param slot The slot.
   */
  private empty(slot: number): void {
    this.figures[slot] = null;
    this.names[slot] = null;
    this.held -= 1;
  }

  /**
   * The slot held longest.
   *
   * @returns Its index.
   */
  private oldest(): number {
    let oldest = 0;
    for (let slot = 1; slot < this.figures.length; slot += 1) {
      if ((this.since[slot] as number) < (this.since[oldest] as number)) {
        oldest = slot;
      }
    }
    return oldest;
  }
}
